import { renderCurrencyInputField } from 'components/FormFields'
import { renderSearchableSelectField } from 'components/FormFields'
import AccountSearch from 'components/AccountSearch'
import SimpleFileFolder from 'components/SimpleFileFolder'
import SectionHeader from '../../Distributions/components/Header/SectionHeader.js'
import styles from '../../Distributions/components/Main/styles.css'
import { CASH_ACCOUNT_OPTIONS, DEPOSIT_TYPE_OPTIONS, BANK_ACCOUNTS } from './constant.js'
import { useFeeData, createFeeFormatter, isDepositOld, isDepositTooFarInFuture } from './depositUtils.js'
import formatters from 'lib/formatters'
import { httpPut } from 'lib/http'
import { useDispatch } from 'react-redux'
import { setBanner, setLoad, setUnload } from 'lib/notification/actions'
import React, { useState } from 'react'

const CommonDepositForm = ({
  form,
  updateForm,
  formDisabled,
  onUploadClick,
  files,
  onDeleteClick,
  shouldShowNote = false,
  initialFeeData = null,
  hasDepositTypeFromAPI = false,
  currentStatus = '',
  nextStatus = '',
  showBankAccountField = true,
  showDepositToCashOption = false,
  depositId = null,
}) => {
  const dispatch = useDispatch()
  const [isRecalculating, setIsRecalculating] = useState(false)
  const { feeData, isLoadingFees } = useFeeData(form, initialFeeData, updateForm)
  const formatFeeValue = createFeeFormatter(isLoadingFees)

  const handleRecalculateFees = () => {
    if (!depositId || isRecalculating) return
    setIsRecalculating(true)
    dispatch(setLoad())
    httpPut(`/admin/deposits/${depositId}/recalculate_fees`)
      .then(() => {
        window.location.reload()
      })
      .catch((err) => {
        setIsRecalculating(false)
        dispatch(setUnload())
        dispatch(setBanner(err?.message || 'Failed to recalculate fees. Please try again.', 'danger'))
      })
  }

  const handleAccountSelect = (account) => {
    updateForm({ target: { name: 'trust_account', value: account } })

    if (account === null) {
      updateForm({ target: { name: 'account', value: null } })
    }
  }

  const handleDepositToCashChange = (value) => {
    if (value === true) {
      updateForm({ target: { name: 'trust_account', value: null } })

      updateForm({ target: { name: 'account', value: null } })
    }
    updateForm({ target: { name: 'deposit_to_cash', value } })
  }

  const handleCashAccountChange = (e) => {
    updateForm({ target: { name: 'cash_account', value: e.target.value } })
  }

  const handleAmountChange = (value) => {
    updateForm({ target: { name: 'amount', value: value } })
  }

  const getAccountDisplayValue = () => {
    if (showDepositToCashOption && form.deposit_to_cash === true) {
      return ''
    }

    if (form.trust_account && form.trust_account.number) {
      return `${form.trust_account.number} - ${form.trust_account.beneficiary}`
    }
    if (form.account && form.account.number) {
      return `${form.account.number} - ${form.account.beneficiary}`
    }
    return ''
  }

  const shouldShowCashAlert = () => {
    const alertSteps = ['received', 'quick_deposited', 'transferred']
    return alertSteps.includes(currentStatus) || (nextStatus && alertSteps.includes(nextStatus))
  }

  const shouldShowFileWarning = () => {
    const hasNoFiles = !files || files.length === 0
    return hasNoFiles && nextStatus == 'reviewed'
  }

  const isDepositTypeDisabled = formDisabled || hasDepositTypeFromAPI

  const disabledDelete = ['deposited', 'cash', 'deleted'].includes(form.status)
  const hideRecalculateFees = ['deposited', 'deleted'].includes(form.status || currentStatus)

  return (
    <div className={styles.distributionFormContainer}>
      <div className={styles.sectionOne}>
        <section className={styles.cardMarginBot}>
          <SectionHeader title="Deposit Information" />
          <section className={styles.sectionNoMargin}>
            <section className={styles.subSection}>
              <div className={styles.formItemMono}>
                <label className={styles.label} htmlFor="deposit_type">
                  Deposit Type *
                </label>
                <select
                  name="deposit_type"
                  value={form.deposit_type}
                  onChange={updateForm}
                  disabled={isDepositTypeDisabled}
                  className={styles.select}
                  required
                >
                  {DEPOSIT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {hasDepositTypeFromAPI && (
                  <div className={styles.fieldNote}>
                    <small>Deposit type cannot be changed after ticket is created</small>
                  </div>
                )}
              </div>
            </section>

            <section className={styles.subSection}>
              <div className={styles.fileUpload}>
                <div className={styles.fileUploadComponent}>
                  <SimpleFileFolder
                    onUploadClick={onUploadClick}
                    files={files}
                    onDeleteClick={onDeleteClick}
                    disabledDelete={disabledDelete}
                    disableUpload={disabledDelete}
                  />
                </div>
                {shouldShowFileWarning() && (
                  <div className={styles.rowError}>
                    <span className={styles.error}>Document upload is requireds</span>
                  </div>
                )}
              </div>
            </section>

            {form.copied_from_id && (
              <section className={styles.subSection}>
                <a
                  href={`/deposits/${form.copied_from_id}`}
                  className={styles.originalDepositLink}
                  style={{ textDecoration: 'none' }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Original CASH deposit received: {formatters.formatOriginalDepositDate(form.copied_from_date)}, ID {form.copied_from_id}
                </a>
              </section>
            )}

            {showBankAccountField && (
              <section className={styles.subSection}>
                <div className={styles.formItemMono}>
                  {renderSearchableSelectField({
                    input: {
                      name: 'deposited_bank_account',
                      value: form.deposited_bank_account || '',
                      onChange: (value) => updateForm({ target: { name: 'deposited_bank_account', value } }),
                    },
                    meta: {},
                    label: 'Bank Account Where Funds Were Deposited',
                    options: BANK_ACCOUNTS,
                    placeholderText: 'Search and select bank account...',
                    disabled: formDisabled,
                  })}
                </div>
              </section>
            )}

            {(nextStatus == 'transferred' || form.status == 'transferred') && form.receiving_bank_account && (
              <section className={styles.subSection}>
                <div className={styles.formItemMono}>
                  <label className={styles.label} htmlFor="receiving_bank_account">
                    Bank Account Where Funds are to be Transferred
                  </label>
                  <input
                    name="receiving_bank_account"
                    type="text"
                    value={form.receiving_bank_account || ''}
                    disabled={true}
                    readOnly={true}
                    className={styles.input}
                  />
                  <div className={styles.rowError}>
                    <span className={styles.error}>Transfer is needed</span>
                  </div>
                </div>
              </section>
            )}

            <section className={styles.subSection}>
              <div className={styles.formItemMono}>
                <label className={styles.label} htmlFor="bank_date">
                  Deposit Date
                </label>
                <input name="bank_date" type="date" value={form.bank_date} onChange={updateForm} disabled={formDisabled} className={styles.input} />
                {isDepositOld(form.bank_date) && (
                  <div className={styles.rowError}>
                    <div className={styles.orange}>Deposit is more than 15 days old</div>
                  </div>
                )}
                {isDepositTooFarInFuture(form.bank_date) && (
                  <div className={styles.rowError}>
                    <span className={styles.orange}>Deposit date cannot be more than 2 weeks in the future</span>
                  </div>
                )}
              </div>
            </section>

            <section className={styles.subSection}>
              <div className={styles.formItemMono}>
                {renderCurrencyInputField({
                  input: {
                    name: 'amount',
                    value: form.amount || '',
                    onChange: handleAmountChange,
                  },
                  meta: {},
                  label: 'Deposit Amount',
                  placeholder: '0.00',
                  disabled: formDisabled,
                  flattenValue: true,
                  decimalScale: 2,
                  fixedDecimalScale: true,
                })}
              </div>
            </section>

            <section className={styles.subSection}>
              <div className={styles.formItemMono}>
                <label className={styles.label} htmlFor="trust_account">
                  {showDepositToCashOption ? 'Client Account number' : 'Account number'}
                </label>
                <div className={styles.searchComponent}>
                  <AccountSearch
                    onAccountSelect={handleAccountSelect}
                    disabled={formDisabled || (showDepositToCashOption && form.deposit_to_cash === true)}
                    valueInsideInput={getAccountDisplayValue()}
                    initialAccount={form.account}
                    key={showDepositToCashOption && form.deposit_to_cash ? 'disabled' : 'enabled'}
                  />
                </div>
              </div>
            </section>

            {(nextStatus == 'quick_deposited' || form.status == 'quick_deposited') && (
              <section className={styles.subSection}>
                <div className={styles.formItemMono}>
                  <label className={styles.label} htmlFor="receiving_bank_account">
                    Bank Account to Deposit Funds
                  </label>
                  <input
                    name="receiving_bank_account"
                    type="text"
                    value={form.receiving_bank_account || ''}
                    disabled={true}
                    readOnly={true}
                    className={styles.input}
                  />
                </div>
              </section>
            )}

            {showDepositToCashOption && (
              <>
                <section className={styles.subSection}>
                  <div className={styles.radioButtonGroup}>
                    <label className={styles.label}>Deposit into CASH account - Choose yes only if the client is unknown</label>
                    <div className={styles.buttons}>
                      <input
                        type="radio"
                        name="deposit_to_cash"
                        value={true}
                        checked={form.deposit_to_cash === true}
                        onChange={() => handleDepositToCashChange(true)}
                        disabled={formDisabled}
                      />
                      <label>Yes</label>
                      <input
                        type="radio"
                        name="deposit_to_cash"
                        value={false}
                        checked={form.deposit_to_cash === false}
                        onChange={() => handleDepositToCashChange(false)}
                        disabled={formDisabled}
                      />
                      <label>No</label>
                    </div>
                  </div>
                </section>

                {form.deposit_type === 'check' && form.deposit_to_cash === true && (
                  <>
                    {shouldShowCashAlert() && (
                      <div className={styles.orange}>Please consult with a senior admin prior to depositing into the Cash Account.</div>
                    )}
                    <section className={styles.subSection}>
                      <div className={styles.formItemMono}>
                        <label className={styles.label} htmlFor="cash_account">
                          Choose Trust
                        </label>
                        <select
                          name="cash_account"
                          value={form.cash_account || ''}
                          onChange={handleCashAccountChange}
                          disabled={formDisabled}
                          className={`${styles.select} ${!form.cash_account ? styles.selectPlaceholder : ''}`}
                        >
                          {CASH_ACCOUNT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </section>
                  </>
                )}
              </>
            )}

            {shouldShowNote && (
              <section className={styles.subSection}>
                <div className={styles.formItemMono}>
                  <label className={styles.label} htmlFor="note">
                    Add a note
                  </label>
                  <textarea
                    name="note"
                    value={form.note || ''}
                    onChange={updateForm}
                    disabled={formDisabled}
                    className={styles.textarea}
                    placeholder="Enter any additional notes..."
                    rows={3}
                  />
                </div>
              </section>
            )}
          </section>
        </section>
      </div>

      <div className={styles.sectionTwo}>
        <section className={styles.cardMarginBot}>
          <SectionHeader title="Fee Removal Information" />
          <section className={styles.sectionNoMargin}>
            <p className={styles.noteText}>
              <strong>Note:</strong> If the deposit is refund to the client, or an investment transfer, no fee will be taken from the client's
              account.
            </p>
            <section className={styles.subSection}>
              <div className={styles.radioButtonGroup}>
                <label className={styles.label}>Is this a refund?</label>
                <div className={styles.buttons}>
                  <input
                    type="radio"
                    name="is_refund"
                    value="true"
                    checked={form.is_refund === true}
                    onChange={(e) => updateForm({ target: { name: 'is_refund', value: true } })}
                    disabled={formDisabled}
                  />
                  <label>Yes</label>
                  <input
                    type="radio"
                    name="is_refund"
                    value="false"
                    checked={form.is_refund === false}
                    onChange={(e) => updateForm({ target: { name: 'is_refund', value: false } })}
                    disabled={formDisabled}
                  />
                  <label>No</label>
                </div>
              </div>
            </section>

            <section className={styles.subSection}>
              <div className={styles.radioButtonGroup}>
                <label className={styles.label}>Is this an investment transfer?</label>
                <div className={styles.buttons}>
                  <input
                    type="radio"
                    name="is_transfer"
                    value="true"
                    checked={form.is_transfer === true}
                    onChange={(e) => updateForm({ target: { name: 'is_transfer', value: true } })}
                    disabled={formDisabled}
                  />
                  <label>Yes</label>
                  <input
                    type="radio"
                    name="is_transfer"
                    value="false"
                    checked={form.is_transfer === false}
                    onChange={(e) => updateForm({ target: { name: 'is_transfer', value: false } })}
                    disabled={formDisabled}
                  />
                  <label>No</label>
                </div>
              </div>
            </section>

            {form.is_refund === true && form.is_transfer === true && (
              <section className={styles.subSection}>
                <div className={styles.rowError}>
                  <span className={styles.error}>A deposit cannot be both a refund and an investment transfer.</span>
                </div>
              </section>
            )}
          </section>
        </section>

        <section className={styles.cardMarginBot}>
          <SectionHeader title="Applied Administrative Fee" />
          <section className={styles.sectionNoMargin}>
            <div className={styles.feeInfoRow}>
              <div className={styles.feeInfoItem}>
                <label className={styles.label}>Fee Schedule</label>
                <div className={styles.feeValue}>{formatFeeValue(feeData.fee_schedule, 'text')}</div>
              </div>
              <div className={styles.feeInfoItem}>
                <label className={styles.label}>Balance used for Fee Calculation</label>
                <div className={styles.feeValue}>{formatFeeValue(feeData.total_balance_snapshot, 'money')}</div>
              </div>
            </div>

            <div className={styles.feeInfoRow}>
              <div className={styles.feeInfoItem}>
                <label className={styles.label}>Admin Fee</label>
                <div className={styles.feeValue}>{formatFeeValue(feeData.fee_amount, 'money')}</div>
              </div>
              <div className={styles.feeInfoItem}>
                <label className={styles.label}>Fee Rate</label>
                <div className={styles.feeValue}>{formatFeeValue(feeData.fee_rate, 'percentage')}</div>
              </div>
            </div>

            <div className={styles.feeInfoRow}>

              <div className={styles.feeInfoItem}>
                <label className={styles.label}>Enrollment Fee</label>
                <div className={styles.feeValue}>{formatFeeValue(feeData.enrollment_fee_amount, 'money')}</div>
              </div>
              {depositId && !hideRecalculateFees && (
                <div className={styles.feeInfoItem}>
                  <button
                    type="button"
                    className={styles.recalculateFeeLink}
                    onClick={handleRecalculateFees}
                    disabled={isRecalculating}
                  >
                    {isRecalculating ? 'Recalculating…' : 'Recalculate fees'}
                  </button>
                </div>
              )}
            </div>
          </section>
        </section>

        {(feeData.calculated_enrollment_fee_amount || form.calculated_enrollment_fee_amount) > 0 && (
          <section className={styles.cardMarginBot}>
            <SectionHeader title="Enrollment Fee" />
            <section className={styles.sectionNoMargin}>
              <section className={styles.subSection}>
                <div className={styles.radioButtonGroup}>
                  <label className={styles.label}>Take enrollment fee from this deposit?</label>
                  <div className={styles.buttons}>
                    <input
                      type="radio"
                      name="take_enrollment_fee"
                      value="true"
                      checked={form.take_enrollment_fee === true}
                      onChange={(e) => updateForm({ target: { name: 'take_enrollment_fee', value: true } })}
                      disabled={formDisabled}
                    />
                    <label>Yes</label>
                    <input
                      type="radio"
                      name="take_enrollment_fee"
                      value="false"
                      checked={form.take_enrollment_fee === false}
                      onChange={(e) => updateForm({ target: { name: 'take_enrollment_fee', value: false } })}
                      disabled={formDisabled}
                    />
                    <label>No</label>
                  </div>
                </div>
              </section>
            </section>
          </section>
        )}
      </div>
    </div>
  )
}

export default CommonDepositForm
